import { useEffect, useMemo, useState } from 'react';
import type { RootState } from '@/store';
import {
  Alert,
  Box,
  Button,
  CardContent,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Slider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  USER_LOAN_PROGRAM,
  USER_OCCUPANCY,
  userLoanProgramMap,
} from '@/config/constants';
import { useCreateLoanApplicationMutation } from '@/apis/extended/loanApplicationApi';
import { useGetProductQuery } from '@/apis/extended/productApi';
import {
  useGetRateAlertsQuery,
  useUpsertRateAlertMutation,
} from '@/apis/extended/rateAlertApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { ProductDetailEstimation } from '@/components/ProductDetail/ProductDetailEstimation';
import { GlassCard } from '@/components/ui/GlassCard';
import { useHybridInterestRate } from '@/hooks/useHybridInterestRate';
import { setRefinanceState } from '@/store/slices/refinanceSlice';
import { calculateHELOANClosingDisclosure } from '@/utils/calculator/HELOAN';
import { calculateHELOCClosingDisclosure } from '@/utils/calculator/HELOC';
import { calculateSecondMortgageClosingDisclosure } from '@/utils/calculator/SecondMortgage';
import { computeKeepVsRefiComparison } from '@/utils/keepVsRefiComparison';
import {
  calculateBridgeRefinanceClosingDisclosure,
  calculateCommercialClosingDisclosure,
  calculateConventionalClosingDisclosure,
  calculateDefaultClosingDisclosure,
  calculateDSCRClosingDisclosure,
  calculateFHAClosingDisclosure,
  calculateFixAndFlipClosingDisclosure,
  calculateUSDAClosingDisclosure,
  calculateVAClosingDisclosure,
} from '@/utils/refinanceCalculator';
import { REFI_TYPE_LABELS } from '@/utils/refinanceDocs';
import {
  getRefinanceImpliedEquityDown,
  mergeRefinanceSliceIntoProduct,
} from '@/utils/refinanceEstimationFromProduct';
import { computeRefinanceScenarios } from '@/utils/refinanceScenarios';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const percent = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 2,
});

const formatCurrency = (value: number) =>
  currency.format(Number.isFinite(value) ? value : 0);

const formatPercent = (value: number) =>
  percent.format(Number.isFinite(value) ? value / 100 : 0);

const RATE_SNAPSHOTS_KEY_PREFIX = 'refinance_rate_snapshots_';
const MAX_SNAPSHOTS = 50;

export type RateSnapshot = {
  date: string;
  rate: number;
  program?: string;
  currentMortgageRate?: number;
};

function loadRateSnapshots(productId: string | undefined): RateSnapshot[] {
  if (!productId) return [];
  try {
    const raw = localStorage.getItem(
      `${RATE_SNAPSHOTS_KEY_PREFIX}${productId}`
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RateSnapshot[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveRateSnapshots(productId: string, snapshots: RateSnapshot[]) {
  try {
    localStorage.setItem(
      `${RATE_SNAPSHOTS_KEY_PREFIX}${productId}`,
      JSON.stringify(snapshots.slice(-MAX_SNAPSHOTS))
    );
  } catch (e) {
    console.error('Failed to save rate snapshots', e);
  }
}

const selectEstimationState = createSelector(
  [
    (state: RootState) => state.interestRate,
    (state: RootState) => state.hybridInterestRate,
  ],
  (interestRate, hybridInterestRate) => ({ interestRate, hybridInterestRate })
);

export const RefinanceSummaryPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [showDocs, setShowDocs] = useState(false);
  const [rateSnapshots, setRateSnapshots] = useState<RateSnapshot[]>([]);
  const [spreadBps, setSpreadBps] = useState(25);

  const refinance = useSelector((state: RootState) => state.refinance);
  const estimationState = useSelector(selectEstimationState);

  const { data: currentUser } = useGetCurrentUserQuery();
  const rateWatchOnly = currentUser?.onboardingIntent === 'rate_watch';
  const { data: product, isLoading: isProductLoading } = useGetProductQuery(
    productId ?? '',
    { skip: !productId }
  );
  const [createLoanApplication, { isLoading: creatingApprovalApp }] =
    useCreateLoanApplicationMutation();
  const { data: rateAlerts } = useGetRateAlertsQuery(undefined, {
    skip: !productId,
  });
  const [upsertRateAlert, { isLoading: savingAlert }] =
    useUpsertRateAlertMutation();

  const serverAlert = useMemo(
    () =>
      productId
        ? rateAlerts?.find((a) => a.propertyId === productId && a.enabled)
        : undefined,
    [rateAlerts, productId]
  );

  useEffect(() => {
    if (serverAlert && typeof serverAlert.spreadBps === 'number') {
      setSpreadBps(serverAlert.spreadBps);
    }
  }, [serverAlert]);
  // Populates state.hybridInterestRate from the real GET /interest-rates/current
  // endpoint (see ProductDetail.tsx for the same fix) — replaces a fetch from
  // /interest-rates/matrices, which has no backing backend route.
  useHybridInterestRate();

  useEffect(() => {
    if (productId && product?.refinance) {
      dispatch(
        setRefinanceState({
          currentLoan: {
            rate:
              product.refinance.currentLoan?.rate ?? refinance.currentLoan.rate,
            balance:
              product.refinance.currentLoan?.balance ??
              refinance.currentLoan.balance,
            termYears:
              product.refinance.currentLoan?.termYears ??
              refinance.currentLoan.termYears,
            monthlyPayment:
              product.refinance.currentLoan?.monthlyPayment ??
              refinance.currentLoan.monthlyPayment,
            escrowTaxes:
              product.refinance.currentLoan?.escrowTaxes ??
              refinance.currentLoan.escrowTaxes,
            escrowInsurance:
              product.refinance.currentLoan?.escrowInsurance ??
              refinance.currentLoan.escrowInsurance,
            escrowHoa:
              product.refinance.currentLoan?.escrowHoa ??
              refinance.currentLoan.escrowHoa,
          },
          property: {
            address:
              product.refinance.property?.street ?? refinance.property.address,
            city: product.refinance.property?.city ?? refinance.property.city,
            state:
              product.refinance.property?.state ?? refinance.property.state,
            zipCode:
              product.refinance.property?.zip ?? refinance.property.zipCode,
            value:
              product.refinance.property?.value ?? refinance.property.value,
            propertyType:
              product.refinance.property?.propertyType ??
              refinance.property.propertyType,
          },
          newLoan: {
            termYears:
              product.refinance.newLoan?.termYears ??
              refinance.newLoan.termYears,
            creditScore:
              product.refinance.newLoan?.creditScore ??
              refinance.newLoan.creditScore,
            cashOutAmount:
              product.refinance.newLoan?.cashOutAmount ??
              refinance.newLoan.cashOutAmount,
            requestedLoanAmount:
              product.refinance.newLoan?.requestedLoanAmount ??
              refinance.newLoan.requestedLoanAmount,
          },
          selection: {
            program:
              (product.refinance.selection?.program as USER_LOAN_PROGRAM) ??
              refinance.selection.program,
            refiType:
              (product.refinance.selection?.refiType as any) ??
              refinance.selection.refiType,
            occupancy:
              (product.refinance.selection?.occupancy as USER_OCCUPANCY) ??
              refinance.selection.occupancy,
          },
          statement: {
            escrowBalance:
              product.refinance.statement?.escrowBalance ??
              refinance.statement.escrowBalance,
            buydownBalance:
              product.refinance.statement?.buydownBalance ??
              refinance.statement.buydownBalance,
            prepaymentPenalty:
              product.refinance.statement?.prepaymentPenalty ??
              refinance.statement.prepaymentPenalty,
            lastPaymentDate:
              product.refinance.statement?.lastPaymentDate ??
              refinance.statement.lastPaymentDate,
            estimatedPayoff:
              product.refinance.statement?.estimatedPayoff ??
              refinance.statement.estimatedPayoff,
          },
        })
      );
    }
  }, [dispatch, product, productId]);

  const newLoanAmount = useMemo(() => {
    if (
      ['heloc', 'heloan', 'second_mortgage'].includes(
        refinance.selection.refiType
      )
    ) {
      return (
        refinance.newLoan.requestedLoanAmount || refinance.currentLoan.balance
      );
    }

    if (refinance.selection.refiType === 'cash_out') {
      return (
        refinance.currentLoan.balance + (refinance.newLoan.cashOutAmount || 0)
      );
    }

    return refinance.currentLoan.balance;
  }, [
    refinance.currentLoan.balance,
    refinance.newLoan.cashOutAmount,
    refinance.newLoan.requestedLoanAmount,
    refinance.selection.refiType,
  ]);

  const estimationInput = useMemo(() => {
    const equityLineOrLoan =
      refinance.newLoan.requestedLoanAmount || refinance.currentLoan.balance;
    return {
      offerPrice: refinance.property.value,
      listPrice: refinance.property.value,
      downPayment: Math.max(0, refinance.property.value - newLoanAmount),
      zipCode: Number(refinance.property.zipCode) || 0,
      hoaFees: refinance.currentLoan.escrowHoa,
      commission: 0,
      marketInterestRate: refinance.currentLoan.rate,
      creditScore: refinance.newLoan.creditScore,
      loanTerm: refinance.newLoan.termYears,
      currentLoanBalance: refinance.currentLoan.balance,
      requestedLoanAmount: equityLineOrLoan,
      requestedCreditLine: equityLineOrLoan,
    };
  }, [
    refinance.currentLoan.balance,
    refinance.currentLoan.escrowHoa,
    refinance.currentLoan.rate,
    refinance.newLoan.creditScore,
    refinance.newLoan.requestedLoanAmount,
    refinance.newLoan.termYears,
    refinance.property.value,
    refinance.property.zipCode,
    newLoanAmount,
  ]);

  const estimation = useMemo(() => {
    const state = estimationState as RootState;
    switch (refinance.selection.program) {
      case USER_LOAN_PROGRAM.CONVENTIONAL:
        return calculateConventionalClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.FHA:
        return calculateFHAClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.VA:
        return calculateVAClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.USDA:
        return calculateUSDAClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.DSCR:
        return calculateDSCRClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.FIX_AND_FLIP:
        return calculateFixAndFlipClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.COMMERCIAL:
        return calculateCommercialClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.BRIDGE: {
        const totalLoan =
          estimationInput.offerPrice - estimationInput.downPayment;
        const existingLoanBalance = refinance.currentLoan.balance;
        const cashOutAmount = Math.max(0, totalLoan - existingLoanBalance);
        const loanTermMonths = Math.min(
          24,
          Math.max(6, estimationInput.loanTerm * 12)
        );
        return calculateBridgeRefinanceClosingDisclosure(
          {
            currentPropertyValue: estimationInput.offerPrice,
            existingLoanBalance,
            cashOutAmount,
            zipCode: estimationInput.zipCode,
            hoaFees: estimationInput.hoaFees,
            marketInterestRate: estimationInput.marketInterestRate,
            creditScore: estimationInput.creditScore,
            loanTerm: loanTermMonths,
          },
          state
        );
      }
      case USER_LOAN_PROGRAM.HELOC:
        return calculateHELOCClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.HELOAN:
        return calculateHELOANClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.SECOND_MORTGAGE:
        return calculateSecondMortgageClosingDisclosure(estimationInput, state);
      default:
        return calculateDefaultClosingDisclosure(estimationInput, state);
    }
  }, [
    estimationState,
    estimationInput,
    refinance.selection.program,
    refinance.currentLoan.balance,
  ]);

  const currentMonthlyTotal =
    refinance.currentLoan.monthlyPayment +
    refinance.currentLoan.escrowTaxes +
    refinance.currentLoan.escrowInsurance +
    refinance.currentLoan.escrowHoa;
  const estMetrics = estimation as {
    piti?: number;
    closingCosts?: number;
    principalAndInterest?: number;
    loanAmount?: number;
    loanTerm?: number;
    totalInterest?: number;
    interestRate?: number;
  } | null;
  const newMonthlyTotal = estMetrics?.piti ?? 0;
  const closingCosts = estMetrics?.closingCosts ?? 0;

  const newLifetimeInterest = useMemo(() => {
    if (
      estMetrics?.totalInterest != null &&
      Number(estMetrics.totalInterest) > 0
    ) {
      return Number(estMetrics.totalInterest);
    }
    const pAndI = Number(estMetrics?.principalAndInterest ?? 0);
    const loanAmt = Number(estMetrics?.loanAmount ?? newLoanAmount);
    const termYears = Number(
      estMetrics?.loanTerm ?? refinance.newLoan.termYears
    );
    return Math.max(0, pAndI * termYears * 12 - loanAmt);
  }, [
    estMetrics?.totalInterest,
    estMetrics?.principalAndInterest,
    estMetrics?.loanAmount,
    estMetrics?.loanTerm,
    newLoanAmount,
    refinance.newLoan.termYears,
  ]);

  const keepVsRefi = useMemo(
    () =>
      computeKeepVsRefiComparison({
        refinance,
        newLoanAmount,
        newMonthlyTotal,
        newLifetimeInterest,
        closingCosts,
      }),
    [
      refinance,
      newLoanAmount,
      newMonthlyTotal,
      newLifetimeInterest,
      closingCosts,
    ]
  );

  const monthlyDelta = keepVsRefi.monthlyDelta;
  const breakevenMonths = keepVsRefi.breakevenMonths;

  const marketVsCurrentSpread =
    (estMetrics?.interestRate ?? 0) > 0
      ? refinance.currentLoan.rate - (estMetrics?.interestRate ?? 0)
      : null;

  const missingInputs =
    refinance.property.value <= 0 || refinance.currentLoan.balance <= 0;

  const scenarios = useMemo(() => {
    if (missingInputs) return [];
    return computeRefinanceScenarios(
      refinance,
      estimationState as RootState,
      refinance.selection.program
    );
  }, [refinance, estimationState, missingInputs]);

  // No per-program document checklist source exists yet: this read from the
  // old /interest-rates/matrices endpoint, which had no backing route (always
  // 404), so this checklist has always been empty in practice.
  const checklist: string[] = [];

  const productForLoanEstimate = useMemo(() => {
    if (
      !product ||
      product.transactionMode !== 'refinance' ||
      !product.refinance
    ) {
      return null;
    }
    return mergeRefinanceSliceIntoProduct(product, refinance);
  }, [product, refinance]);

  const loanEstimateComparisons = useMemo(() => {
    if (!productForLoanEstimate) return [];
    return [
      {
        loanProgram: refinance.selection.program,
        downPayment: getRefinanceImpliedEquityDown(productForLoanEstimate),
      },
    ];
  }, [productForLoanEstimate, refinance.selection.program]);

  useEffect(() => {
    setRateSnapshots(loadRateSnapshots(productId ?? undefined));
  }, [productId]);

  const handleSaveCurrentRate = () => {
    if (!productId) return;
    const rate = estimation?.interestRate ?? 0;
    const program = refinance.selection.program;
    const next = [
      ...rateSnapshots,
      {
        date: new Date().toISOString(),
        rate,
        program,
        currentMortgageRate: refinance.currentLoan.rate,
      },
    ].slice(-MAX_SNAPSHOTS);
    setRateSnapshots(next);
    saveRateSnapshots(productId, next);
  };

  const handleSaveRateWatch = async () => {
    if (!productId) return;
    const currentRate = refinance.currentLoan.rate;
    const threshold = currentRate - spreadBps / 100;
    try {
      await upsertRateAlert({
        propertyId: productId,
        threshold,
        currentRatePct: currentRate,
        spreadBps,
        loanProgram: String(refinance.selection.program || 'conventional'),
        enabled: true,
      }).unwrap();
      toast.success(
        `Watching rates ${spreadBps} bps below your ${currentRate.toFixed(
          2
        )}% mortgage`
      );
    } catch (err) {
      console.error('Failed to save rate watch', err);
      toast.error('Failed to save rate watch');
    }
  };

  const sectionCardSx = {
    borderRadius: 3,
    border: `1px solid ${theme.palette.greyShades.border1}`,
    color: 'text.primary',
    boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
  };

  const cardContentSx = {
    p: { xs: 2, sm: 3, md: 4 },
    color: 'text.primary',
  };

  if (productId && isProductLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="body1" color="text.secondary">
          Loading refinance summary...
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            Refinance Summary
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              mt: 0.5,
            }}
          >
            {rateWatchOnly && serverAlert
              ? `You're watching rates — we'll notify you when market drops ${
                  serverAlert.spreadBps ?? spreadBps
                } bps below your ${formatPercent(
                  refinance.currentLoan.rate
                )} mortgage.`
              : 'Compare your current loan to your refinance options.'}
          </Typography>
        </Box>
        {productId && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {rateWatchOnly ? (
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={() => navigate(`/refinance/intake/${productId}`)}
                startIcon={<i className="fa-light fa-pen" />}
                sx={{ fontWeight: 600, borderRadius: 2 }}
              >
                Add loan details for full comparison
              </Button>
            ) : null}
            <Button
              variant={rateWatchOnly ? 'outlined' : 'contained'}
              color="primary"
              size="medium"
              disabled={creatingApprovalApp}
              onClick={async () => {
                try {
                  const app = await createLoanApplication({
                    flowProfile: 'refinance',
                    productType: 'conventional',
                    sourceProductId: productId,
                    approvalChannel: 'refinance_summary',
                  }).unwrap();
                  navigate(`/loan/${app._id}`);
                } catch {
                  toast.error('Could not start mortgage approval application.');
                }
              }}
              startIcon={<i className="fa-light fa-file-lines" />}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              Mortgage approval application
            </Button>
            {!rateWatchOnly ? (
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() => navigate(`/refinance/intake/${productId}`)}
                startIcon={<i className="fa-light fa-pen" />}
                sx={{ fontWeight: 600, borderRadius: 2 }}
              >
                Edit intake
              </Button>
            ) : null}
          </Box>
        )}
      </Box>

      {missingInputs && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the refinance intake so we can generate a full
          comparison.
          <Button
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() =>
              navigate(
                productId
                  ? `/refinance/intake/${productId}`
                  : '/refinance/intake'
              )
            }
          >
            Go to Intake
          </Button>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <GlassCard variant="medium" sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                pb: 1,
              }}
            >
              Comparison Analysis
            </Typography>
            <CardContent sx={cardContentSx}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Current Loan
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                    }}
                  >
                    {formatPercent(refinance.currentLoan.rate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Balance: {formatCurrency(refinance.currentLoan.balance)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Term: {refinance.currentLoan.termYears} years
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly: {formatCurrency(currentMonthlyTotal)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Refinance Estimate
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                    }}
                  >
                    {formatPercent(estimation?.interestRate || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New Loan: {formatCurrency(newLoanAmount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Term: {refinance.newLoan.termYears} years
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly: {formatCurrency(newMonthlyTotal)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2, borderColor: 'divider' }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Monthly Delta
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {formatCurrency(monthlyDelta)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Estimated Closing Costs
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {formatCurrency(closingCosts)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Break-even (Months)
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {breakevenMonths !== null ? breakevenMonths : '—'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2, borderColor: 'divider' }} />

              <Typography
                variant="subtitle2"
                sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 1 }}
              >
                Keep vs refinance (remaining amortization)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Remaining interest if you keep
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {formatCurrency(keepVsRefi.currentRemainingInterest)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Over {keepVsRefi.currentRemainingMonths} months
                    {keepVsRefi.usedDerivedPayment
                      ? ' (P&I derived from balance/rate/term)'
                      : ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Lifetime interest on new loan
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {formatCurrency(keepVsRefi.newLifetimeInterest)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Interest delta:{' '}
                    {formatCurrency(keepVsRefi.interestSavingsIfRefi)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Payoff vs new loan
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {formatCurrency(keepVsRefi.effectivePayoff)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Net cash at close:{' '}
                    {formatCurrency(keepVsRefi.netCashAtClose)}
                    {keepVsRefi.netCashAtClose < 0
                      ? ' (to you)'
                      : keepVsRefi.netCashAtClose > 0
                      ? ' (you bring)'
                      : ''}
                  </Typography>
                </Grid>
              </Grid>
              {marketVsCurrentSpread != null && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Market quote vs your mortgage:{' '}
                  {marketVsCurrentSpread >= 0
                    ? `${formatPercent(marketVsCurrentSpread)} lower`
                    : `${formatPercent(
                        Math.abs(marketVsCurrentSpread)
                      )} higher`}
                </Typography>
              )}
            </CardContent>
          </GlassCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <GlassCard variant="medium" sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                pb: 1,
              }}
            >
              Refinance Details
            </Typography>
            <CardContent sx={cardContentSx}>
              <Typography variant="body2" color="text.secondary">
                Program: {userLoanProgramMap[refinance.selection.program]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Refi Type: {REFI_TYPE_LABELS[refinance.selection.refiType]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Property Value: {formatCurrency(refinance.property.value)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Loan-to-Value:{' '}
                {refinance.property.value > 0
                  ? formatPercent(
                      (newLoanAmount / refinance.property.value) * 100
                    )
                  : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Credit Score: {refinance.newLoan.creditScore}
              </Typography>

              <Divider sx={{ my: 2, borderColor: 'divider' }} />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => setShowDocs(true)}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Move Forward
              </Button>
            </CardContent>
          </GlassCard>
        </Grid>

        <Grid item xs={12}>
          <GlassCard variant="medium" sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                pb: 1,
              }}
            >
              Rate tracking
            </Typography>
            <CardContent sx={cardContentSx}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Compare the estimated quote to your current mortgage rate (
                {formatPercent(refinance.currentLoan.rate)}). Save snapshots and
                set a watch that fires when market rates drop {spreadBps} bps
                below your rate.
              </Typography>
              {marketVsCurrentSpread != null && (
                <Alert
                  severity={marketVsCurrentSpread > 0 ? 'success' : 'info'}
                  sx={{ mb: 2 }}
                >
                  {marketVsCurrentSpread > 0
                    ? `Today's estimate is ${formatPercent(
                        marketVsCurrentSpread
                      )} below your current mortgage.`
                    : `Today's estimate is still ${formatPercent(
                        Math.abs(marketVsCurrentSpread)
                      )} above your current mortgage.`}
                </Alert>
              )}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={<i className="fa-light fa-chart-line" />}
                  onClick={handleSaveCurrentRate}
                  disabled={missingInputs || estimation?.interestRate == null}
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                >
                  Save quote vs current
                </Button>
              </Stack>
              {productId && (
                <Box sx={{ mb: 3, maxWidth: 420 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Rate watch vs your mortgage
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Notify me when rates are {spreadBps} bps below{' '}
                    {formatPercent(refinance.currentLoan.rate)} (at or under{' '}
                    {formatPercent(
                      refinance.currentLoan.rate - spreadBps / 100
                    )}
                    )
                  </Typography>
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={spreadBps}
                    onChange={(_, v) => setSpreadBps(v as number)}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v} bps`}
                    sx={{ mt: 1 }}
                    disabled={missingInputs}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSaveRateWatch}
                    disabled={missingInputs || savingAlert || !productId}
                    sx={{ fontWeight: 600, borderRadius: 2 }}
                  >
                    {serverAlert ? 'Update rate watch' : 'Enable rate watch'}
                  </Button>
                  {serverAlert && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      Active watch: market ≤{' '}
                      {formatPercent(
                        (serverAlert.currentRatePct ??
                          refinance.currentLoan.rate) -
                          (serverAlert.spreadBps ?? 0) / 100
                      )}
                    </Typography>
                  )}
                </Box>
              )}
              {rateSnapshots.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No rate snapshots yet. Save a quote above to track it against
                  your current mortgage.
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {[...rateSnapshots].reverse().map((s, i) => (
                    <Box component="li" key={`${s.date}-${i}`} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(s.date).toLocaleDateString(undefined, {
                          dateStyle: 'medium',
                        })}
                        {' — quote '}
                        {formatPercent(s.rate)}
                        {s.currentMortgageRate != null
                          ? ` vs mortgage ${formatPercent(
                              s.currentMortgageRate
                            )} (${
                              s.currentMortgageRate - s.rate >= 0
                                ? `${formatPercent(
                                    s.currentMortgageRate - s.rate
                                  )} lower`
                                : `${formatPercent(
                                    s.rate - s.currentMortgageRate
                                  )} higher`
                            })`
                          : ''}
                        {s.program != null &&
                        userLoanProgramMap[
                          s.program as keyof typeof userLoanProgramMap
                        ] != null
                          ? ` (${
                              userLoanProgramMap[
                                s.program as keyof typeof userLoanProgramMap
                              ]
                            })`
                          : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </GlassCard>
        </Grid>

        {!missingInputs && scenarios.length > 0 && (
          <Grid item xs={12}>
            <GlassCard variant="medium" sx={sectionCardSx}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  px: { xs: 2, sm: 3, md: 4 },
                  pt: { xs: 2, sm: 3, md: 4 },
                  pb: 1,
                }}
              >
                Scenario options
              </Typography>
              <CardContent sx={cardContentSx}>
                <Grid container spacing={2}>
                  {scenarios.map((s) => (
                    <Grid item xs={12} md={4} key={s.id}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          height: '100%',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          {s.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 1 }}
                        >
                          {s.subtitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          New loan: {formatCurrency(s.newLoanAmount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Monthly: {formatCurrency(s.monthlyPiti)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Savings: {formatCurrency(s.monthlySavings)}/mo
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Break-even:{' '}
                          {s.breakEvenMonths != null
                            ? `${s.breakEvenMonths} mo`
                            : '—'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Lifetime interest:{' '}
                          {formatCurrency(s.lifetimeInterest)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </GlassCard>
          </Grid>
        )}

        {!missingInputs && productForLoanEstimate && (
          <Grid item xs={12}>
            <Box sx={{ mt: { xs: 1, md: 2 } }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                }}
              >
                Loan estimate
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Detailed closing cost breakdown for your selected refinance
                program ({userLoanProgramMap[refinance.selection.program]}).
              </Typography>
              <ProductDetailEstimation
                product={productForLoanEstimate}
                comparisons={loanEstimateComparisons}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={showDocs}
        onClose={() => setShowDocs(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Documentation Checklist</DialogTitle>
        <DialogContent dividers>
          {checklist.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No program-specific documents are configured yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {checklist.map((item: string, index: number) => (
                <FormControlLabel
                  key={`${item}-${index}`}
                  control={<Checkbox />}
                  label={item}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDocs(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
